import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import StudyShell from "@/components/study/StudyShell";
import ProtectedContent from "@/components/study/ProtectedContent";
import {
  startSprint,
  saveSprintAnswer,
  submitSprint,
  trackEvent,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const LETTERS = ["A", "B", "C", "D"];

function fmtClock(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function StudySprintRunner({ sprintType }) {
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const endsAtRef = useRef(0);
  const submittedRef = useRef(false);
  const answersRef = useRef({});
  const attemptRef = useRef(null);

  // --- Submit (used by button and timer auto-submit) ---
  const doSubmit = useCallback(
    async (auto) => {
      const a = attemptRef.current;
      if (!a || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      try {
        await submitSprint(a.attempt_id, answersRef.current, Boolean(auto));
        trackEvent(auto ? "timed_sprint_auto_submitted" : "timed_sprint_submitted", {
          sprint_type: a.sprint_type,
          attempt_id: a.attempt_id,
        });
        if (auto) toast.message("Time's up — your sprint was submitted automatically.");
        navigate(`/study/mini-exams/results/${a.attempt_id}`);
      } catch (e) {
        submittedRef.current = false;
        setSubmitting(false);
        toast.error("Could not submit your sprint. Please try again.");
      }
    },
    [navigate]
  );

  // --- Start / resume the attempt on mount ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await startSprint(sprintType);
        if (cancelled) return;
        setAttempt(a);
        attemptRef.current = a;
        setAnswers(a.answers || {});
        answersRef.current = a.answers || {};
        endsAtRef.current = Date.now() + (a.remaining_seconds || 0) * 1000;
        setRemaining(a.remaining_seconds || 0);
        trackEvent("timed_sprint_started", {
          sprint_type: a.sprint_type,
          attempt_id: a.attempt_id,
          resumed: a.resumed,
        });
        if (a.warning) {
          // Dev/admin-facing notice surfaced in console; users see a soft banner.
          if (process.env.NODE_ENV !== "production") {
            console.warn("Sprint question shortage:", a.warning);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            (e && e.response && e.response.data && e.response.data.detail) ||
              "Could not start this sprint."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sprintType]);

  // --- Countdown timer (recomputed from endsAt to avoid drift) ---
  useEffect(() => {
    if (!attempt) return undefined;
    const tick = () => {
      const left = Math.max(0, Math.round((endsAtRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0 && !submittedRef.current) {
        doSubmit(true);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt, doSubmit]);

  // --- Warn before leaving while a sprint is in progress ---
  useEffect(() => {
    const handler = (e) => {
      if (attemptRef.current && !submittedRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const selectAnswer = (qid, letter) => {
    if (submittedRef.current) return;
    const next = { ...answersRef.current, [qid]: letter };
    answersRef.current = next;
    setAnswers(next);
    saveSprintAnswer(attemptRef.current.attempt_id, qid, letter).catch(() => {});
    trackEvent("timed_sprint_question_answered", {
      attempt_id: attemptRef.current.attempt_id,
      question_id: qid,
    });
  };

  if (loading) {
    return (
      <StudyShell testId={STUDY.sprintRunner}>
        <p className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">
          Preparing your sprint...
        </p>
      </StudyShell>
    );
  }

  if (error || !attempt) {
    return (
      <StudyShell testId={STUDY.sprintRunner}>
        <div className="rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center">
          <p className="text-base font-semibold text-[var(--ibp-ink)]">
            {error || "Sprint unavailable."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/study/mini-exams")}
            className="mt-4 inline-flex rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white"
          >
            Back to Exam Mode
          </button>
        </div>
      </StudyShell>
    );
  }

  const questions = attempt.questions || [];
  const total = questions.length;
  const q = questions[index];
  const answeredCount = Object.keys(answers).length;
  const unanswered = total - answeredCount;
  const lowTime = remaining <= 60;
  const isSimulation = attempt.kind === "simulation";
  const isMarathon = attempt.kind === "marathon";
  const attemptLabel = isSimulation
    ? "Exact-length exam simulation"
    : isMarathon
    ? "Combined stamina session"
    : "Timed Review Sprint";
  const submitLabel = isSimulation || isMarathon ? "Submit simulation" : "Submit sprint";

  return (
    <StudyShell testId={STUDY.sprintRunner}>
      {/* Header: title + timer */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--ibp-ink)]">{attempt.sprint_title}</h1>
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
            {attemptLabel}
          </p>
        </div>
        <div
          data-testid={STUDY.sprintTimer}
          className={[
            "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-lg font-bold tabular-nums",
            lowTime
              ? "bg-[color:rgba(155,44,44,0.1)] text-[#9b2c2c]"
              : "bg-[var(--ibp-navy-950)] text-white",
          ].join(" ")}
        >
          <Clock className="h-5 w-5" /> {fmtClock(remaining)}
        </div>
      </div>

      {attempt.warning ? (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#d9a441] bg-[color:rgba(214,162,30,0.08)] p-3 text-xs text-[#7a5a12]">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
          <span>Note: {attempt.warning}. This sprint uses all available active questions.</span>
        </div>
      ) : null}

      {/* Question palette */}
      <div className="mt-5 flex flex-wrap gap-1.5">
        {questions.map((qq, qi) => {
          const isCur = qi === index;
          const isAns = Boolean(answers[qq.id]);
          return (
            <button
              key={qq.id}
              type="button"
              onClick={() => setIndex(qi)}
              className={[
                "h-8 w-8 rounded-lg text-xs font-bold transition-colors",
                isCur
                  ? "bg-[var(--ibp-gold-500)] text-[var(--ibp-ink)]"
                  : isAns
                  ? "bg-[var(--ibp-navy-950)] text-white"
                  : "bg-white text-[var(--ibp-ink)] border border-[color:rgba(10,27,51,0.15)]",
              ].join(" ")}
            >
              {qi + 1}
            </button>
          );
        })}
      </div>

      {/* Question card */}
      <ProtectedContent
        data-testid={STUDY.questionCard}
        className="mt-5 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8"
      >
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-[var(--ibp-ink)]">
            Question {index + 1} of {total}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-[var(--ibp-navy-950)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
              {q.topic}
            </span>
          </div>
        </div>

        <h2 className="mt-5 text-lg font-semibold leading-7 text-[var(--ibp-ink)]">
          {q.question_text}
        </h2>

        <div className="mt-5 space-y-3">
          {LETTERS.map((letter) => {
            const isSel = answers[q.id] === letter;
            return (
              <button
                key={letter}
                type="button"
                data-testid={`${STUDY.sprintChoice}-${letter}`}
                onClick={() => selectAnswer(q.id, letter)}
                className={[
                  "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                  isSel
                    ? "border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.12)]"
                    : "border-[color:rgba(10,27,51,0.15)] bg-white hover:border-[var(--ibp-navy-950)]",
                ].join(" ")}
              >
                <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-[var(--ibp-offwhite)] text-sm font-bold text-[var(--ibp-ink)]">
                  {letter}
                </span>
                <span className="text-sm leading-6 text-[var(--ibp-ink)]">{q.choices[letter]}</span>
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            data-testid={STUDY.sprintPrev}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.2)] px-5 py-2.5 text-sm font-semibold text-[var(--ibp-ink)] disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {index < total - 1 ? (
            <button
              type="button"
              data-testid={STUDY.sprintNext}
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
            >
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              data-testid={STUDY.sprintSubmit}
              onClick={() => setShowConfirm(true)}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-2.5 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> {submitLabel}
            </button>
          )}
        </div>
      </ProtectedContent>

      {/* Submit bar (always available) */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white px-5 py-3">
        <p className="text-sm text-[color:rgba(11,18,32,0.7)]">
          Answered <span className="font-bold text-[var(--ibp-ink)]">{answeredCount}</span> of {total}
          {unanswered > 0 ? (
            <span className="ml-1 text-[#9b2c2c]">({unanswered} unanswered)</span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          <Send className="h-4 w-4" /> {submitLabel}
        </button>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-[var(--ibp-ink)]">
              Submit your {isFullMock ? "mock exam" : "sprint"}?
            </h3>
            <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.7)]">
              {unanswered > 0
                ? `You still have ${unanswered} unanswered question(s). Unanswered questions are marked incorrect.`
                : "You've answered every question. Once submitted, answers are locked."}
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="rounded-xl border border-[color:rgba(10,27,51,0.2)] px-5 py-2.5 text-sm font-semibold text-[var(--ibp-ink)]"
              >
                Keep working
              </button>
              <button
                type="button"
                data-testid={STUDY.sprintConfirmSubmit}
                onClick={() => {
                  setShowConfirm(false);
                  doSubmit(false);
                }}
                disabled={submitting}
                className="rounded-xl bg-[var(--ibp-gold-500)] px-5 py-2.5 text-sm font-semibold text-[var(--ibp-ink)] disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </StudyShell>
  );
}
